import { Cog6ToothIcon } from '@heroicons/react/24/solid'
import {
  ChangeEvent,
  ClipboardEvent,
  FC,
  ReactNode,
  useEffect,
  useId,
  useRef,
  useState
} from 'react'

import { cn } from '@/app/utils'
import AppInputMessage from '@/components/shared/input/ui/AppInputMessage'

import styles from './AutoNumberInput.module.css'
import { AutoNumberInputMasked } from './AutoNumberInputMasked'

type Props = {
  value?: string
  isStandard: boolean
  onChange: (value: string, skipValidation?: boolean) => void
  onStandardChange: (next: boolean) => void
  onBlur?: () => void
  invalid?: boolean
  invalidMessage?: ReactNode
  className?: string
  boxClassName?: string
  dataTestId?: string
}

const parseStandardValue = (value: string | null) => {
  if (value === null) {
    return { autoNumber: '', region: '' }
  }

  const trimmed = value?.trim() ?? ''

  // Формат без пробелов: «E123ТТ777» или «E123ТТ»
  const withoutSpacesMatch =
    /^([A-Za-zА-я])(\d{3})([A-Za-zА-я]{2})(\d{1,3})?$/.exec(trimmed)

  if (withoutSpacesMatch) {
    return {
      autoNumber: `${withoutSpacesMatch[1]} ${withoutSpacesMatch[2]} ${withoutSpacesMatch[3]}`,
      region: withoutSpacesMatch[4] ?? ''
    }
  }

  // Формат с пробелами «E 123 ТТ 777»
  const withSpacesMatch =
    /^([A-Za-zА-я]\s\d{3}\s[A-Za-zА-я]{2})\s(\d{1,3})$/.exec(trimmed)

  if (withSpacesMatch) {
    return { autoNumber: withSpacesMatch[1], region: withSpacesMatch[2] }
  }

  // Проверка для региона
  if (/^\d{1,3}$/.test(trimmed)) {
    return { autoNumber: '', region: trimmed }
  }

  return { autoNumber: trimmed, region: '' }
}

// Используется для проверки значений из буфера обмена при вставке в инпут.
// Допускает как «E 123 ТТ 777», так и «E123ТТ777».
const pasteAutoNumber =
  /^([A-ZА-Я])\s*(\d{3})\s*([A-ZА-Я]{2})(?:\s*(\d{1,3}))?$/
const pasteRegion = /^\d{1,3}$/

const AUTO_NUMBER_LABEL = 'Номер ТС'

const AutoNumberInput: FC<Props> = ({
  value,
  isStandard,
  onChange,
  onStandardChange,
  onBlur,
  invalid,
  invalidMessage,
  className,
  boxClassName,
  dataTestId
}) => {
  const autoNumberInputId = useId()
  const regionInputId = useId()
  const customInputId = useId()

  const regionInputRef = useRef<HTMLInputElement | null>(null)
  const lastEmittedValue = useRef<string>('')

  const [autoNumberValue, setAutoNumberValue] = useState(() =>
    isStandard ? parseStandardValue(value ?? '').autoNumber : ''
  )
  const [regionValue, setRegionValue] = useState(() =>
    isStandard ? parseStandardValue(value ?? '').region : ''
  )
  const [customValue, setCustomValue] = useState(() =>
    isStandard ? '' : value ?? ''
  )

  useEffect(() => {
    if (value === lastEmittedValue.current) {
      return
    }

    if (isStandard) {
      const parsed = parseStandardValue(value ?? '')
      setAutoNumberValue(prev =>
        prev === parsed.autoNumber ? prev : parsed.autoNumber
      )
      setRegionValue(prev => (prev === parsed.region ? prev : parsed.region))
    } else {
      setCustomValue(prev => (prev === (value ?? '') ? prev : value ?? ''))
    }
  }, [value, isStandard])

  const joinStandardValue = (autoNumber: string, region: string) => {
    const autoNumberWithoutSpaces = autoNumber.replaceAll(/\s+/g, '')
    const regionWithoutSpaces = region.replaceAll(/\s+/g, '')
    return `${autoNumberWithoutSpaces}${regionWithoutSpaces}`
  }

  const handleAutoNumberAccept = (value: string) => {
    setAutoNumberValue(value)
    const joined = joinStandardValue(value, regionValue)
    lastEmittedValue.current = joined
    onChange(joined)
  }

  const handleAutoNumberComplete = () => {
    if (regionInputRef.current) {
      regionInputRef.current.focus()
    }
  }

  const handleRegionAccept = (value: string) => {
    setRegionValue(value)
    const joined = joinStandardValue(autoNumberValue, value)
    lastEmittedValue.current = joined
    onChange(joined)
  }

  const handleCustomChange = (event: ChangeEvent<HTMLInputElement>) => {
    const nextValue = event.target.value
    setCustomValue(nextValue)
    lastEmittedValue.current = nextValue
    onChange(nextValue)
  }

  const applyFullPaste = (autoNumber: string, region: string) => {
    setAutoNumberValue(autoNumber)
    setRegionValue(region)
    const joined = joinStandardValue(autoNumber, region)
    lastEmittedValue.current = joined
    onChange(joined)
  }

  const normalizePasteValue = (raw: string) =>
    raw.trim().replaceAll(/\s+/g, ' ').toUpperCase()

  const handleAutoNumberPaste = (event: ClipboardEvent<HTMLInputElement>) => {
    const text = normalizePasteValue(event.clipboardData.getData('text'))
    const match = pasteAutoNumber.exec(text)

    if (match?.[4]) {
      event.preventDefault()
      applyFullPaste(`${match[1]} ${match[2]} ${match[3]}`, match[4])
      regionInputRef.current?.focus()
      return
    }

    if (!match) {
      event.preventDefault()
    }
  }

  const handleRegionPaste = (event: ClipboardEvent<HTMLInputElement>) => {
    const text = normalizePasteValue(event.clipboardData.getData('text'))
    const match = pasteAutoNumber.exec(text)

    if (match?.[4]) {
      event.preventDefault()
      applyFullPaste(`${match[1]} ${match[2]} ${match[3]}`, match[4])
      return
    }

    if (!pasteRegion.test(text)) {
      event.preventDefault()
    }
  }

  const handleToggleMode = () => {
    const nextIsStandard = !isStandard
    const nextValue = nextIsStandard
      ? joinStandardValue(autoNumberValue, regionValue)
      : customValue

    lastEmittedValue.current = nextValue
    onStandardChange(nextIsStandard)
    onChange(nextValue, true)
  }

  const parseAutoNumberMask = (
    unmaskedValue: string
  ): { char: string; filled: boolean }[] => {
    const AUTO_NUMBER_MASK_LAYOUT = {
      placeholders: ['А', '0', '0', '0', 'А', 'А'],
      pattern: [0, ' ', 1, 2, 3, ' ', 4, 5]
    }

    return AUTO_NUMBER_MASK_LAYOUT.pattern.map(part => {
      if (typeof part === 'string') {
        return { char: part, filled: true }
      }

      const filled = part < unmaskedValue.length

      return {
        char: filled
          ? unmaskedValue[part]
          : AUTO_NUMBER_MASK_LAYOUT.placeholders[part],
        filled
      }
    })
  }

  return (
    <div className={cn('w-full', className)}>
      <div
        data-testid={dataTestId}
        className={cn(
          'flex h-56 overflow-hidden rounded-12 border border-grey-20 bg-white md:h-48',
          invalid ? '!border-red-80' : 'border-grey-20',
          boxClassName
        )}
      >
        {isStandard ? (
          <>
            <label
              htmlFor={autoNumberInputId}
              className="flex min-w-0 flex-1 cursor-text flex-col justify-center px-12 py-4 md:py-0"
            >
              <span className="font-pro text-grey-60 text-12">
                {AUTO_NUMBER_LABEL}
              </span>
              <div className={styles.maskedField}>
                <div
                  className={cn(styles.maskedOverlay, 'font-bold text-24')}
                  aria-hidden
                >
                  {parseAutoNumberMask(
                    autoNumberValue.replaceAll(/\s+/g, '')
                  ).map(({ filled, char }, index) => (
                    <span
                      key={index}
                      className={
                        filled ? styles.maskedFilled : styles.maskedPlaceholder
                      }
                    >
                      {char}
                    </span>
                  ))}
                </div>
                <AutoNumberInputMasked
                  id={autoNumberInputId}
                  mask="A 000 AA"
                  definitions={{ A: /[A-Za-zА-я]/ }}
                  prepare={(str: string) => str.toUpperCase()}
                  lazy
                  autoComplete="off"
                  aria-label={`${AUTO_NUMBER_LABEL}, формат А 000 АА`}
                  value={autoNumberValue}
                  onAccept={handleAutoNumberAccept}
                  onComplete={handleAutoNumberComplete}
                  onBlur={onBlur}
                  onPaste={handleAutoNumberPaste}
                  className={cn(
                    'w-full font-bold outline-none text-24',
                    styles.maskedInput
                  )}
                />
              </div>
            </label>
            <label
              htmlFor={regionInputId}
              className="flex w-[80px] shrink-0 cursor-text flex-col items-center justify-end border-l border-grey-10 px-12 py-4 md:py-0"
            >
              <div className={styles.maskedField}>
                {regionValue.length < 2 ? (
                  <div
                    className={cn(
                      styles.maskedOverlay,
                      styles.maskedPlaceholder,
                      styles.regionChars,
                      'pl-8 font-bold text-24'
                    )}
                    aria-hidden
                  >
                    {regionValue.length === 1 ? (
                      <>
                        <span className="invisible">0</span>
                        {'00'}
                      </>
                    ) : (
                      '000'
                    )}
                  </div>
                ) : null}
                <AutoNumberInputMasked
                  id={regionInputId}
                  inputRef={node => {
                    regionInputRef.current = node as HTMLInputElement
                  }}
                  mask="000"
                  lazy={true}
                  autoComplete="off"
                  aria-label="Регион"
                  value={regionValue}
                  onAccept={handleRegionAccept}
                  onBlur={onBlur}
                  onPaste={handleRegionPaste}
                  className={cn(
                    'relative z-[1] w-full bg-transparent pl-8 font-bold outline-none text-24',
                    styles.regionChars,
                    regionValue.length === 0 && styles.maskedInput
                  )}
                />
              </div>
            </label>
          </>
        ) : (
          <label
            htmlFor={customInputId}
            className="flex flex-1 cursor-text flex-col justify-center px-12 py-4 md:py-0"
          >
            <span className="font-pro text-grey-60 text-12">
              {AUTO_NUMBER_LABEL}
            </span>
            <input
              id={customInputId}
              data-testid={
                dataTestId && `${dataTestId.replace(/-id$/, '')}-field-id`
              }
              type="text"
              autoComplete="off"
              placeholder="- - - - - - - -"
              value={customValue}
              onChange={handleCustomChange}
              onBlur={onBlur}
              className={cn(
                'w-full bg-transparent font-bold outline-none text-24',
                styles.placeholder
              )}
            />
          </label>
        )}
      </div>
      {invalid && invalidMessage ? (
        <AppInputMessage
          message={invalidMessage}
          iconClassName="text-red-70 flex-shrink-0 pt-2"
          messageClassName="text-red-70"
          className="items-start"
        />
      ) : null}
      <button
        type="button"
        onClick={handleToggleMode}
        className="mt-8 flex items-center gap-8"
      >
        <Cog6ToothIcon className="h-20 w-20 text-bronze-40" />
        <span className="font-pro leading-[24px] text-bronze-40 underline text-16">
          {isStandard ? 'Нестандартный госномер' : 'Стандартный госномер'}
        </span>
      </button>
    </div>
  )
}

export { AutoNumberInput }
